import { useState } from "react";
import { toast } from "sonner";

import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { formatDate, formatDateTime } from "@buildpy/core/lib/format";
import {
  AS_REQUEST_SCHEDULE_MESSAGE,
  canMoveAsRequest,
  type AsRequestStatus,
  type CustomerAsRequest,
} from "@buildpy/core/modules/as-request/rules";
import { setAsRequestStatus } from "@/api/actions/as-request";
import { useSignedUrls } from "@/lib/signed-url";
import { AS_STATUS_TONE } from "@/modules/as-request/ui/status-tone";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

interface Props {
  projectId: string;
  requests: CustomerAsRequest[];
  open: number;
  className?: string;
}

export const staffAsPhotoPath = (projectId: string, asId: string, index: number): string =>
  `/app/projects/${projectId}/as/${asId}/photos/${index}`;

// datetime-local 값(KST 벽시계) → 서버가 받는 ISO(+09:00) · 비었으면 null.
export const scheduledAtOf = (local: string): string | null => (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local) ? `${local}:00+09:00` : null);

// ISO → datetime-local 값(KST) — 다시 잡을 때 칸에 지금 일시를 채운다.
const localOf = (iso: string | null): string => (iso ? formatDateTime(iso, "").replace(" ", "T") : "");

const Photos = ({ srcOf, paths }: { srcOf: (path: string) => string | undefined; paths: string[] }) => (
  <ul className="grid grid-cols-4 gap-1">
    {paths.map((path, i) => {
      const src = srcOf(path);
      return (
        <li key={path}>
          {src ? (
            <a className="block aspect-square overflow-hidden rounded-md" href={src} rel="noopener" target="_blank">
              <img alt={`A/S 사진 ${i + 1}`} className="size-full object-cover" loading="lazy" src={src} />
            </a>
          ) : (
            // 서명 주소가 오기 전엔 자리만 잡는다.
            <span aria-label="사진 불러오는 중" className="block aspect-square animate-pulse rounded-md bg-muted" role="img" />
          )}
        </li>
      );
    })}
  </ul>
);

const Item = ({
  projectId,
  request,
  srcOf,
}: {
  projectId: string;
  request: CustomerAsRequest;
  srcOf: (path: string) => string | undefined;
}) => {
  const { pending, run } = useRecordAction();
  const [picking, setPicking] = useState(false);
  const [when, setWhen] = useState(localOf(request.scheduled_at));
  const paths = Array.from({ length: request.photo_count }, (_, i) => staffAsPhotoPath(projectId, request.id, i));

  const move = (to: AsRequestStatus, scheduledAt: string | null = null) =>
    void run(
      "A/S 상태를 바꾸지 못했습니다",
      () => setAsRequestStatus({ project_id: projectId, id: request.id, from: request.status, status: to, scheduled_at: scheduledAt }),
      () => {
        setPicking(false);
        toast.success("A/S 상태를 바꿨습니다");
      }
    );

  const schedule = () => {
    const at = scheduledAtOf(when);
    if (!at) {
      toast.error(AS_REQUEST_SCHEDULE_MESSAGE);
      return;
    }
    move("SCHEDULED", at);
  };

  return (
    <li className="flex flex-col gap-y-1.5 border-t border-border pt-3 first:border-t-0 first:pt-0" data-testid="project-as-item">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{request.category}</span>
        <Pill tone={AS_STATUS_TONE[request.status]}>{request.status_label}</Pill>
      </div>
      <p className="whitespace-pre-wrap break-words">{request.body}</p>
      {request.status === "SCHEDULED" && request.scheduled_at && (
        <p className="text-xs font-medium">방문 예정 {formatDateTime(request.scheduled_at)}</p>
      )}
      {paths.length > 0 && <Photos paths={paths} srcOf={srcOf} />}
      <p className="text-xs text-muted-foreground">접수 {formatDate(request.created_at)}</p>

      {picking && (
        <div className="flex items-end gap-x-2" data-testid="project-as-schedule">
          <Input
            className="min-w-0 flex-1"
            disabled={pending}
            inputSize="sm"
            label="방문 일시"
            name={`as-schedule-${request.id}`}
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
          <Button disabled={pending || when === ""} size="sm" onClick={schedule}>
            정하기
          </Button>
        </div>
      )}

      {request.status !== "DONE" && (
        <div className="flex flex-wrap gap-1.5">
          {canMoveAsRequest(request.status, "SCHEDULED") && !picking && (
            <Button data-testid="project-as-schedule-open" disabled={pending} size="compact" variant="outline" onClick={() => setPicking(true)}>
              {request.status === "SCHEDULED" ? "일시 바꾸기" : "방문 예정"}
            </Button>
          )}
          {canMoveAsRequest(request.status, "DONE") && (
            <Button data-testid="project-as-done" disabled={pending} size="compact" variant="outline" onClick={() => move("DONE")}>
              처리 완료
            </Button>
          )}
          {canMoveAsRequest(request.status, "OPEN") && (
            <Button data-testid="project-as-reopen" disabled={pending} size="compact" variant="ghost-muted" onClick={() => move("OPEN")}>
              접수로 되돌리기
            </Button>
          )}
          {picking && (
            <Button disabled={pending} size="compact" variant="ghost-muted" onClick={() => setPicking(false)}>
              취소
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

// 현장 A/S — 고객이 링크로 낸 접수 · 접수 → 방문 예정(일시) → 처리 완료 · 사진은 서명 주소로.
const StaffAsCard = ({ projectId, requests, open, className }: Props) => {
  const srcOf = useSignedUrls(requests.flatMap((r) => Array.from({ length: r.photo_count }, (_, i) => staffAsPhotoPath(projectId, r.id, i))));
  return (
    <SectionCard className={className} data-testid="project-as-card" padding="sm">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        A/S
        {open > 0 && <Pill tone="new">진행 {open}</Pill>}
      </h2>
      {requests.length === 0 ? (
        <p className="text-muted-foreground" data-testid="project-as-empty">
          고객이 낸 A/S 가 없습니다.
        </p>
      ) : (
        <ul className="flex flex-col gap-y-3">
          {requests.map((r) => (
            <Item key={r.id} projectId={projectId} request={r} srcOf={srcOf} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
};

export default StaffAsCard;
